import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  ArrowLeft, Loader2, AlertCircle, CheckCircle2, Download, FileText,
  Save, Sliders, Rocket,
} from 'lucide-react';
import { accountingAPI } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { toast } from 'sonner';
import { WorkflowStepper } from '@/components/accounting/WorkflowStepper';
import { NextActionCard } from '@/components/accounting/NextActionCard';
import { MappingWizard } from '@/components/accounting/MappingWizard';
import ReconciliationPanel from '@/components/accounting/ReconciliationPanel';
import { UnmatchedAgentsCard } from '@/components/accounting/UnmatchedAgentsCard';
import { BankDepositCard } from '@/components/accounting/BankDepositCard';
import GenerateStatementsDialog from '@/components/accounting/GenerateStatementsDialog';
import GeneratedStatementsCard from '@/components/accounting/GeneratedStatementsCard';

const REQUIRED_FIELDS = [
  ['carrier_name', 'Carrier'],
  ['product', 'Product'],
  ['agent_npn', 'Agent NPN'],
  ['effective_date', 'Effective Date'],
  ['application_date', 'Application Date'],
  ['received_date', 'Received Date'],
  ['amount_received_by_kr', 'Amount Received by KR'],
];

// Amount To Pay is captured during Reconciliation, not during Mapping.
const OPTIONAL_FIELDS = [
  ['amount_to_pay', 'Amount To Pay'],
  ['agent_name', 'Agent Name'],
  ['termination_date', 'Termination Date'],
  ['policy_number', 'Policy #'],
  ['member_name', 'Member Name'],
  ['writing_number', 'Writing Number'],
  ['transaction_type', 'Transaction Type'],
  ['notes', 'Notes'],
];

const STATUS_COLORS = {
  imported:   'bg-slate-100 text-slate-700',
  mapping:    'bg-amber-100 text-amber-800',
  ready:      'bg-blue-100 text-blue-800',
  reconciled: 'bg-emerald-100 text-emerald-800',
  archived:   'bg-slate-200 text-slate-500',
};

export default function CarrierImportDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [imp, setImp] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [needsOnly, setNeedsOnly] = useState(false);
  const [selectedLine, setSelectedLine] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [impRes, linesRes] = await Promise.all([
        accountingAPI.getImport(id),
        accountingAPI.listLines(id, { page_size: 500, ...(needsOnly ? { needs_review: true } : {}) }),
      ]);
      setImp(impRes.data);
      setLines(linesRes.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load import');
    } finally { setLoading(false); }
  }, [id, needsOnly]);

  useEffect(() => { load(); }, [load]);

  const downloadFile = async () => {
    try {
      const token = localStorage.getItem('token') || '';
      const url = `${process.env.REACT_APP_BACKEND_URL}/api${accountingAPI.fileUrl(id)}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('Download failed');
      const b = await r.blob();
      const dl = URL.createObjectURL(b);
      const a = document.createElement('a');
      a.href = dl; a.download = imp?.file_original_filename || 'statement'; a.click();
      URL.revokeObjectURL(dl);
    } catch { toast.error('Failed to download'); }
  };

  const markReady = async () => {
    try {
      await accountingAPI.setStatus(id, 'ready');
      toast.success('Marked as ready for reconciliation');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const saveLine = async () => {
    if (!selectedLine) return;
    setSaving(true);
    try {
      const payload = {};
      REQUIRED_FIELDS.concat(OPTIONAL_FIELDS).forEach(([k]) => {
        payload[k] = selectedLine[k] ?? null;
      });
      const { data } = await accountingAPI.updateLine(id, selectedLine.line_id, payload);
      setSelectedLine(data);
      setLines((prev) => prev.map((l) => (l.line_id === data.line_id ? data : l)));
      toast.success('Line saved');
      const impRes = await accountingAPI.getImport(id);
      setImp(impRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  // Central handler for the "next action" CTA that drives the guided workflow.
  const handleNextAction = async () => {
    const key = imp?.next_action?.key;
    if (!key) return;
    if (key === 'confirm_mapping' || key === 'fill_missing') {
      setMappingOpen(true);
      return;
    }
    if (key === 'mark_ready') {
      markReady();
      return;
    }
    if (key === 'start_reconciliation') {
      try {
        const r = await accountingAPI.reconcile(id);
        toast.success(
          `Reconciled ${r.data.matched} matched · ${r.data.placeholders} placeholder(s) · ${r.data.needs_review} need review`,
        );
        await load();
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Reconciliation failed');
      }
      return;
    }
    if (key === 'continue_reconciliation') {
      // Scroll to reconciliation panel; it's already visible on this page.
      document.querySelector('[data-testid="reconciliation-panel"]')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    if (key === 'generate_statements') {
      setGenerateDialogOpen(true);
      return;
    }
    if (key === 'add_to_batch') {
      // Phase 27.30.4 — direct flow: pre-select the statements produced
      // by this import and land on Payment Batches with the preview
      // dialog already open, so the user doesn't have to hunt for them.
      try {
        const { data } = await accountingAPI.statementsForImport(id);
        const eligibleIds = (data.items || [])
          .filter((s) => (s.status || '').toLowerCase() !== 'batched'
            && (s.status || '').toLowerCase() !== 'paid')
          .map((s) => s.statement_id);
        if (eligibleIds.length === 0) {
          toast.info('All statements from this import are already in a batch or paid.');
          navigate('/accounting/payment-batches');
          return;
        }
        navigate('/accounting/payment-batches', {
          state: {
            preselectStatementIds: eligibleIds,
            importLabel: eligibleIds.length === 1
              ? '1 statement from this import'
              : `${eligibleIds.length} statements from this import`,
          },
        });
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load statements from this import');
        navigate('/accounting/payment-batches');
      }
      return;
    }
  };

  if (loading || !imp) {
    return <div className="p-16 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>;
  }

  return (
    <div className="page-content" data-testid="carrier-import-detail">
      <PageHeader
        title={imp.period_label}
        description={`${imp.carrier_name} · ${imp.file_original_filename} · ${(imp.file_size/1024).toFixed(1)} KB`}
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Accounting', href: '/accounting/carrier-imports' },
          { label: 'Carrier Imports', href: '/accounting/carrier-imports' },
          { label: imp.period_label },
        ]}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setMappingOpen(true)} data-testid="open-mapping-btn">
              <Sliders size={14} className="mr-1" /> Column Mapping
            </Button>
            <Button variant="outline" onClick={downloadFile} data-testid="download-file-btn">
              <Download size={14} className="mr-1" /> Download
            </Button>
            {imp.status !== 'ready' && (
              <Button
                onClick={markReady}
                disabled={imp.lines_with_missing_required > 0 || imp.line_count === 0}
                data-testid="mark-ready-btn"
                title={imp.lines_with_missing_required > 0 ? 'Complete every required field first' : 'Ready for reconciliation'}
              >
                <Rocket size={14} className="mr-1" /> Mark Ready
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate('/accounting/carrier-imports')}>
              <ArrowLeft size={14} className="mr-1" /> Back
            </Button>
          </div>
        }
      />

      {/* Workflow stepper + next action */}
      <div className="mb-4 space-y-2">
        <WorkflowStepper stage={imp.stage} />
        <NextActionCard nextAction={imp.next_action} onClick={handleNextAction} />
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <Card><CardContent className="pt-4">
          <div className="text-xs text-slate-500">Status</div>
          <div><Badge className={STATUS_COLORS[imp.status]}>{imp.status}</Badge></div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-xs text-slate-500">Total lines</div>
          <div className="text-2xl font-semibold">{imp.line_count}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-xs text-slate-500">Needs review</div>
          <div className={`text-2xl font-semibold ${imp.lines_with_missing_required ? 'text-amber-600' : 'text-emerald-600'}`}>
            {imp.lines_with_missing_required}
          </div>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-xs text-slate-500">Uploaded by</div>
          <div className="text-sm">{imp.uploaded_by_name}</div>
          <div className="text-xs text-slate-400">{formatDate(imp.uploaded_at)}</div>
        </CardContent></Card>
      </div>

      {/* Phase 25.3 — Statements generated from this Carrier Import */}
      {imp.statements_count > 0 && (
        <div className="mt-4">
          <GeneratedStatementsCard importId={id} />
        </div>
      )}

      {/* Panel: Reconciliation (25.2) OR Mapped Lines (25.1) */}
      {(imp.status === 'ready' || imp.status === 'reconciled') ? (
        <>
          <div className="mb-4 grid gap-4 md:grid-cols-2">
            <UnmatchedAgentsCard importId={id} onResolved={load} />
            <BankDepositCard importId={id} onConfirmed={load} />
          </div>
          <ReconciliationPanel importDetail={imp} onImportRefresh={load} />
        </>
      ) : (
        <>
          {/* Filter */}
          <div className="flex items-center gap-2 mb-3">
            <Badge variant={!needsOnly ? 'default' : 'outline'}
                   className="cursor-pointer" onClick={() => setNeedsOnly(false)}
                   data-testid="filter-all">All lines</Badge>
            <Badge variant={needsOnly ? 'default' : 'outline'}
                   className="cursor-pointer" onClick={() => setNeedsOnly(true)}
                   data-testid="filter-needs-review">Needs review only</Badge>
          </div>

          {/* Lines table */}
          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Mapped Lines</CardTitle>
              {imp.raw_headers?.length > 0 && (
                <div className="text-xs text-slate-500">
                  {Object.keys(imp.column_map || {}).length} of {imp.raw_headers.length} columns mapped
                  {imp.mapped_template_id && <> · via template</>}
                </div>
              )}
            </CardHeader>
            <CardContent>
              {lines.length === 0 ? (
                <div className="p-10 text-center text-slate-500" data-testid="no-lines-cta">
                  <FileText className="mx-auto mb-2 text-slate-400" size={22} />
                  <div className="mb-3">No lines yet.</div>
                  {imp.raw_headers?.length > 0 ? (
                    <Button onClick={() => setMappingOpen(true)}>
                      <Sliders size={14} className="mr-1" /> Configure Column Mapping
                    </Button>
                  ) : (
                    <div className="text-xs text-slate-400">
                      PDFs must be manually keyed. XLSX/CSV auto-parse on upload.
                    </div>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="lines-table">
                    <thead>
                      <tr className="text-left text-[10px] uppercase text-slate-500 border-b">
                        <th className="py-1.5 px-1">#</th>
                        <th className="py-1.5 px-1">Product</th>
                        <th className="py-1.5 px-1">NPN</th>
                        <th className="py-1.5 px-1">Effective</th>
                        <th className="py-1.5 px-1">App Date</th>
                        <th className="py-1.5 px-1">Received</th>
                        <th className="py-1.5 px-1 text-right">Amt Received</th>
                        <th className="py-1.5 px-1 text-right">Amt To Pay</th>
                        <th className="py-1.5 px-1">Policy</th>
                        <th className="py-1.5 px-1">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((ln) => (
                        <tr
                          key={ln.line_id}
                          onClick={() => setSelectedLine(ln)}
                          className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer"
                          data-testid={`line-row-${ln.line_id}`}
                        >
                          <td className="py-1.5 px-1 font-mono">{ln.line_number}</td>
                          <td className="py-1.5 px-1">{ln.product || <span className="text-red-500">—</span>}</td>
                          <td className="py-1.5 px-1 font-mono">{ln.agent_npn || <span className="text-slate-300">—</span>}</td>
                          <td className="py-1.5 px-1">{ln.effective_date || <span className="text-red-500">—</span>}</td>
                          <td className="py-1.5 px-1">{ln.application_date || <span className="text-red-500">—</span>}</td>
                          <td className="py-1.5 px-1">{ln.received_date || <span className="text-red-500">—</span>}</td>
                          <td className="py-1.5 px-1 text-right font-mono">
                            {ln.amount_received_by_kr != null ? `$${Number(ln.amount_received_by_kr).toFixed(2)}` : <span className="text-red-500">—</span>}
                          </td>
                          <td className="py-1.5 px-1 text-right font-mono">
                            {ln.amount_to_pay != null ? `$${Number(ln.amount_to_pay).toFixed(2)}` : <span className="text-slate-400">—</span>}
                          </td>
                          <td className="py-1.5 px-1 text-xs">{ln.policy_number || ''}</td>
                          <td className="py-1.5 px-1">
                            {(ln.missing_required || []).length > 0 ? (
                              <Badge className="bg-amber-100 text-amber-800 gap-1 text-[10px] py-0 px-1.5">
                                <AlertCircle size={9} /> Review
                              </Badge>
                            ) : (
                              <Badge className="bg-emerald-100 text-emerald-800 gap-1 text-[10px] py-0 px-1.5">
                                <CheckCircle2 size={9} /> OK
                              </Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Mapping wizard */}
      <MappingWizard
        open={mappingOpen}
        onClose={() => setMappingOpen(false)}
        importId={id}
        carrierId={imp.carrier_id}
        onApplied={() => load()}
      />

      {/* Phase 25.3 — Generate Statements confirmation dialog */}
      <GenerateStatementsDialog
        open={generateDialogOpen}
        onClose={() => setGenerateDialogOpen(false)}
        importId={id}
        onGenerated={() => load()}
      />

      {/* Line editor drawer */}
      <Sheet open={!!selectedLine} onOpenChange={(v) => { if (!v) setSelectedLine(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="line-editor">
          <SheetHeader>
            <SheetTitle>Line #{selectedLine?.line_number}</SheetTitle>
            <SheetDescription>Every required field must be filled before the import can move to Reconciliation.</SheetDescription>
          </SheetHeader>

          {selectedLine && (
            <div className="mt-4 space-y-3">
              <div className="text-xs uppercase text-slate-500">Required</div>
              {REQUIRED_FIELDS.map(([key, label]) => (
                <div key={key}>
                  <Label className={selectedLine[key] == null || selectedLine[key] === '' ? 'text-red-600' : ''}>
                    {label}{(selectedLine[key] == null || selectedLine[key] === '') && <span className="ml-1">*</span>}
                  </Label>
                  <Input
                    type={key.startsWith('amount') ? 'number' : (key.endsWith('_date') ? 'date' : 'text')}
                    step={key.startsWith('amount') ? '0.01' : undefined}
                    value={selectedLine[key] ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setSelectedLine({ ...selectedLine, [key]: key.startsWith('amount') ? (v === '' ? null : parseFloat(v)) : v });
                    }}
                    data-testid={`line-field-${key}`}
                  />
                </div>
              ))}

              <div className="text-xs uppercase text-slate-500 mt-4">Optional</div>
              {OPTIONAL_FIELDS.map(([key, label]) => (
                <div key={key}>
                  <Label>{label}</Label>
                  <Input
                    type={key.endsWith('_date') ? 'date' : 'text'}
                    value={selectedLine[key] ?? ''}
                    onChange={(e) => setSelectedLine({ ...selectedLine, [key]: e.target.value })}
                    data-testid={`line-field-${key}`}
                  />
                </div>
              ))}

              <div className="pt-2 flex justify-end">
                <Button onClick={saveLine} disabled={saving} data-testid="line-save-btn">
                  {saving ? 'Saving…' : (<><Save size={14} className="mr-1" /> Save Line</>)}
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
